import {isEmpty, omit} from 'lodash';
import {useMemo, useState} from 'react';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {emojiToUnified} from '../../../srcCommon/helpers/emoji/emojiHelpers';
import {
  EmojiDefinitionAndSkinTone,
  emojiToDefinition,
} from '../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {computeHighlightPickerValues, HighlightPickerValues} from '../../helpers/models/highlightHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {HighlightTypesEnum} from '../../models/api/highlightBaseModel';
import {InboxModel} from '../../models/api/inboxModel';
import {LensModel} from '../../models/api/lensModel';
import {TagModel} from '../../models/api/tagModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {LensesFormTypesEnum, LensFormError, LensFormErrorMap} from '../../store/data/lenses/lensesHelpers';
import {LensTemplate} from '../../store/data/lenses/saveLensActions';
import {getUserNamespace} from '../../store/data/teammates/teammatesSelectors';
import {getEmojiValidationMap} from '../../store/stateless/emojiSelectors';
import {EmojiSelectHandler} from '../common/dropdowns/emoji/emojiDropdown';
import {generateTooManyIngredientError} from './lensesErrorHelpers';

const MAX_ITEM_LIMIT = 50;

export interface LensState {
  name: string;
  onNameChange: HandlerOf<string>;
  selectedNamespace: string | undefined;
  setSelectedNamespace: HandlerOf<string | undefined>;
  onNamespaceSelect: HandlerOf<string>;
  selectedInboxIds: ReadonlyArray<InboxModel['id']>;
  onSelectInboxIdsChange: HandlerOf<ReadonlyArray<InboxModel['id']>>;
  selectedTagIds: ReadonlyArray<TagModel['id']>;
  onSelectTagIdsChange: HandlerOf<ReadonlyArray<TagModel['id']>>;
  selectedTeammateIds: ReadonlyArray<TeammateModel['id']>;
  onSelectTeammateIdsChange: HandlerOf<ReadonlyArray<TeammateModel['id']>>;
  selectedTeammatesToPinIds: ReadonlyArray<TeammateModel['id']>;
  onSelectTeammateToPinIdsChange: HandlerOf<ReadonlyArray<TeammateModel['id']>>;
  highlightColor: PaletteColorsEnum | undefined;
  onColorSelect: HandlerOf<PaletteColorsEnum | undefined>;
  highlightEmoji: EmojiDefinitionAndSkinTone | undefined;
  onEmojiSelect: EmojiSelectHandler;
  buildValidTemplate: () => LensTemplate | null;
  buildValidEditTemplate: () => LensTemplate | null;
  canSave: boolean;
  isDirty: boolean;
  isEditing: boolean;
  highlightDisplayValues: HighlightPickerValues;
  lensFormErrors: LensFormErrorMap;
  addLensFormError: HandlerOf<LensFormError>;
  clearLensFormError: HandlerOf<LensesFormTypesEnum>;
  hasErrorForField: (type: LensesFormTypesEnum) => boolean;
  setIsSaving: HandlerOf<boolean>;
}

interface UseLensStateProps {
  lens?: LensModel;
  baseNamespace?: string;
  isSharedLens?: boolean;
}

export function useLensState({lens, baseNamespace, isSharedLens}: UseLensStateProps): LensState {
  const isEditing = lens !== undefined;
  const emojiValidationMap = useAppSelector(getEmojiValidationMap);
  const userNamespace = useAppSelector(getUserNamespace);

  const [isDirty, setIsDirty] = useState<boolean>(!lens);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState(lens?.name ?? '');
  const onNameChange = (newName: string) => {
    setIsDirty(true);
    setName(newName);
    clearLensFormError(LensesFormTypesEnum.NAME);
  };
  const [selectedTeammatesToPinIds, setSelectedTeammatesToPinIds] = useState<
    ReadonlyArray<TeammateModel['id']>
  >([]);
  const onSelectTeammateToPinIdsChange = (newTeammateIds: ReadonlyArray<TeammateModel['id']>) => {
    setIsDirty(true);
    setSelectedTeammatesToPinIds(newTeammateIds);
  };

  const [selectedNamespace, setSelectedNamespace] = useState<string | undefined>(() => {
    if (baseNamespace) {
      return baseNamespace;
    }
    return isEditing ? lens?.namespace : undefined;
  });
  const [selectedInboxIds, setSelectedInboxIds] = useState<ReadonlyArray<InboxModel['id']>>(
    isEditing ? lens?.inboxIds : [],
  );
  const onSelectInboxIdsChange = (newInboxIds: ReadonlyArray<InboxModel['id']>) => {
    setIsDirty(true);
    setSelectedInboxIds(newInboxIds);

    if (newInboxIds.length > MAX_ITEM_LIMIT) {
      addLensFormError(generateTooManyIngredientError(LensesFormTypesEnum.INBOXES));
    } else {
      clearLensFormError(LensesFormTypesEnum.INBOXES);
    }
    // Clear selected tags and assignees if inbox ids change to none
    if (newInboxIds.length === 0) {
      setSelectedTagIds([]);
      setSelectedTeammateIds([]);
      onSelectTeammateToPinIdsChange([]);
    }
  };
  const [selectedTagIds, setSelectedTagIds] = useState<ReadonlyArray<TagModel['id']>>(
    isEditing ? lens?.tagIds : [],
  );
  const onSelectTagIdsChange = (newTagIds: ReadonlyArray<TagModel['id']>) => {
    setIsDirty(true);
    setSelectedTagIds(newTagIds);

    if (newTagIds.length > MAX_ITEM_LIMIT) {
      addLensFormError(generateTooManyIngredientError(LensesFormTypesEnum.TAGS));
    } else {
      clearLensFormError(LensesFormTypesEnum.TAGS);
    }
  };
  const [selectedTeammateIds, setSelectedTeammateIds] = useState<ReadonlyArray<TeammateModel['id']>>(
    isEditing ? lens?.assigneeIds : [],
  );
  const onSelectTeammateIdsChange = (newTeammateIds: ReadonlyArray<TeammateModel['id']>) => {
    setIsDirty(true);
    setSelectedTeammateIds(newTeammateIds);

    if (newTeammateIds.length > MAX_ITEM_LIMIT) {
      addLensFormError(generateTooManyIngredientError(LensesFormTypesEnum.ASSIGNEES));
    } else {
      clearLensFormError(LensesFormTypesEnum.ASSIGNEES);
    }
  };

  const [highlightColor, setHighlightColor] = useState<PaletteColorsEnum | undefined>(
    isEditing && lens?.type === HighlightTypesEnum.COLOR ? lens?.highlightColor : undefined,
  );
  const [highlightEmoji, setHighlightEmoji] = useState<EmojiDefinitionAndSkinTone | undefined>(
    isEditing && lens?.type === HighlightTypesEnum.EMOJI
      ? emojiToDefinition(lens?.highlightEmoji, emojiValidationMap)
      : undefined,
  );

  /** Lens form error handling */
  const [lensFormErrors, setLensFormErrors] = useState<LensFormErrorMap>({});

  const addLensFormError = (error: LensFormError) => {
    setLensFormErrors({...lensFormErrors, [error.type]: error.message});
  };

  const clearLensFormError = (type: LensesFormTypesEnum) => {
    setLensFormErrors(omit(lensFormErrors, type));
  };

  const hasErrorForField = (type: LensesFormTypesEnum) => Boolean(lensFormErrors[type]);

  /** Returns null if the form is in an invalid state. Otherwise, returns the template. */
  const buildValidTemplate = (): LensTemplate | null => {
    if (!name || !selectedNamespace || !selectedInboxIds.length) {
      return null;
    }

    const baseProps = {
      name,
      namespace: isSharedLens && baseNamespace ? baseNamespace : userNamespace,
      tagIds: selectedTagIds,
      assigneeTeammateIds: selectedTeammateIds,
      inboxIds: selectedInboxIds,
      highlight: highlightEmoji || highlightColor,
      teammatesToPinTo: selectedTeammatesToPinIds,
    };

    if (highlightColor) {
      return {
        ...baseProps,
        highlightColor,
        highlightEmoji: undefined,
        highlightType: HighlightTypesEnum.COLOR,
      };
    }

    if (highlightEmoji) {
      return {
        ...baseProps,
        highlightColor: undefined,
        highlightEmoji: emojiToUnified(highlightEmoji.emoji, highlightEmoji.skinTone),
        highlightType: HighlightTypesEnum.EMOJI,
      };
    }

    return {
      ...baseProps,
      highlightType: HighlightTypesEnum.NONE,
      highlightColor: undefined,
      highlightEmoji: undefined,
    };
  };

  const buildValidEditTemplate = (): LensTemplate | null => {
    const initialTemplate = buildValidTemplate();
    if (!isEditing || !initialTemplate) {
      return null;
    }
    return {
      ...initialTemplate,
      id: lens.id,
    };
  };

  const canSave = useMemo(() => {
    const isPinnedTeammatesRequired = !isEditing && isSharedLens;

    return Boolean(
      !isSaving &&
        isDirty &&
        name.trim().length > 0 &&
        selectedNamespace !== undefined &&
        !isEmpty(selectedInboxIds) &&
        isEmpty(lensFormErrors) &&
        (isPinnedTeammatesRequired ? selectedTeammatesToPinIds.length > 0 : true),
    );
  }, [
    isEditing,
    isSharedLens,
    isSaving,
    isDirty,
    name,
    selectedNamespace,
    selectedInboxIds,
    lensFormErrors,
    selectedTeammatesToPinIds.length,
  ]);

  const onNamespaceSelect: HandlerOf<string> = (newNamespace) => {
    if (selectedNamespace !== newNamespace) {
      // Update the selected namespace.
      setSelectedNamespace(newNamespace);

      // Also clear all ingredient fields. (Consider useReducer instead for tracking these dependent state updates)
      setSelectedInboxIds([]);
      setSelectedTagIds([]);
      // While we don't have to clear assignees because the choices are all company teammate, not all team members,
      // it seems sensible to clear all ingredient fields when changing the team.
      setSelectedTeammateIds([]);
    }
  };

  const highlightDisplayValues = computeHighlightPickerValues(highlightColor, highlightEmoji);

  const onEmojiSelect: EmojiSelectHandler = (_shortcode, definition) => {
    setHighlightColor(undefined);
    setHighlightEmoji(definition);
    setIsDirty(true);
  };

  const onColorSelect: HandlerOf<PaletteColorsEnum | undefined> = (paletteColor) => {
    setHighlightColor(paletteColor);
    setHighlightEmoji(undefined);
    setIsDirty(true);
  };

  return {
    name,
    onNameChange,
    selectedNamespace,
    setSelectedNamespace,
    selectedInboxIds,
    onSelectInboxIdsChange,
    selectedTagIds,
    onSelectTagIdsChange,
    selectedTeammateIds,
    onSelectTeammateIdsChange,
    highlightColor,
    highlightEmoji,
    buildValidTemplate,
    buildValidEditTemplate,
    canSave,
    isDirty,
    isEditing,
    onNamespaceSelect,
    highlightDisplayValues,
    onEmojiSelect,
    onColorSelect,
    selectedTeammatesToPinIds,
    onSelectTeammateToPinIdsChange,
    lensFormErrors,
    addLensFormError,
    clearLensFormError,
    hasErrorForField,
    setIsSaving,
  };
}
